import { useI18n } from 'vue-i18n'
import { z } from 'zod'
import { computed, ref } from 'vue'

import {
  alphabeticAndSpecialCharactersRule,
  alphanumericAndSpecialCharactersRule,
  noSpaceAllowedRule,
} from './useFormater'
import { useSessionStorage } from '@vueuse/core'

/**
 * Amount properties
 */
export const amountProps = ref({
  min: 1000,
  max: 75000,
})

/**
 * Savings properties
 */
export const savings = ref({
  min: 1000,
  max: 75000,
})

export const buildMyselfSchema = (t: ReturnType<typeof useI18n>['t']) => {
  return z.object({
    wrapper: z
      .object({
        amount: z
          .literal('')
          .transform(() => null)
          .refine((val) => val, {
            message: t('myself.formulaire.errors.required', {
              inputType: t('myself.formulaire.amount.label').toLowerCase(),
            }),
          })
          .or(
            z
              .number()
              .min(amountProps.value.min, {
                message: t('myself.formulaire.errors.amountMin', { min: amountProps.value.min }),
              })
              .max(amountProps.value.max, {
                message: t('myself.formulaire.errors.amountMax', { max: amountProps.value.max }),
              })
              .multipleOf(0.01, { message: t('myself.formulaire.errors.invalidDecimal') })
              .nullable()
              .refine((val) => val || val === 0, {
                message: t('myself.formulaire.errors.required', {
                  inputType: t('myself.formulaire.amount.label').toLowerCase(),
                }),
              }),
          ),
        savings: z
          .literal('')
          .transform(() => null)
          .refine((val) => val, {
            message: t('myself.formulaire.errors.required', {
              inputType: 'd',
            }),
          })
          .or(
            z
              .number()
              .min(amountProps.value.min, {
                message: t('myself.formulaire.errors.amountMin', { min: amountProps.value.min }),
              })
              .max(amountProps.value.max, {
                message: t('myself.formulaire.errors.amountMax', { max: amountProps.value.max }),
              })
              .multipleOf(0.01, { message: t('myself.formulaire.errors.invalidDecimal') })
              .nullable()
              .refine((val) => val || val === 0, {
                message: t('myself.formulaire.errors.required', {
                  inputType: t('myself.formulaire.amount.label').toLowerCase(),
                }),
              }),
          ),

        civility: z.string().min(1, { message: t('myself.formulaire.errors.required') }),
        firstname: z
          .string()
          .regex(noSpaceAllowedRule.regex, {
            message: t(noSpaceAllowedRule.message),
          })
          .regex(alphabeticAndSpecialCharactersRule.regex, {
            message: t(alphabeticAndSpecialCharactersRule.message),
          })
          .min(2, { message: t('myself.formulaire.errors.required') })
          .max(32, { message: t('myself.formulaire.errors.max', { max: 32 }) }),
        lastname: z
          .string()
          .regex(noSpaceAllowedRule.regex, {
            message: t(noSpaceAllowedRule.message),
          })
          .regex(alphabeticAndSpecialCharactersRule.regex, {
            message: t(alphabeticAndSpecialCharactersRule.message),
          })
          .min(2, { message: t('myself.formulaire.errors.required') })
          .max(32, { message: t('myself.formulaire.errors.max', { max: 32 }) }),
        birthname: z
          .string()
          .regex(noSpaceAllowedRule.regex, {
            message: t(noSpaceAllowedRule.message),
          })
          .regex(alphabeticAndSpecialCharactersRule.regex, {
            message: t(alphabeticAndSpecialCharactersRule.message),
          })
          .min(2, { message: t('myself.formulaire.errors.required') })
          .max(32, { message: t('myself.formulaire.errors.max', { max: 32 }) })
          .or(z.literal('')) // To avoid control if field empty and not required
          .optional(),

        birthday: z.string().min(1, { message: t('myself.formulaire.errors.required') }),
        birthCity: z
          .string()
          .min(1, { message: t('myself.formulaire.errors.required') })
          .regex(alphanumericAndSpecialCharactersRule.regex, {
            message: t(alphabeticAndSpecialCharactersRule.message),
          }),
        birthCounty: z
          .string()
          .regex(new RegExp(/[0-9]{2}|[0-9]{3}$/i), { message: t('myself.formulaire.errors.formatDepartement') })
          .min(1, { message: t('myself.formulaire.errors.required') }),
        birthCountry: z
          .string()
          .min(1, { message: t('myself.formulaire.errors.required') })
          .regex(alphanumericAndSpecialCharactersRule.regex, {
            message: t(alphabeticAndSpecialCharactersRule.message),
          }),
        phone: z
          .string()
          .regex(new RegExp(/0[67]{1}[0-9]{8}$|^INCONNU$/i), { message: t('myself.formulaire.errors.formatPortable') })
          .min(1, { message: t('myself.formulaire.errors.required') }),
        mail: z
          .string()
          .email(t('myself.formulaire.errors.formatEmail'))
          .min(5, { message: t('myself.formulaire.errors.min', { min: 5 }) })
          .max(70, { message: t('myself.formulaire.errors.max', { min: 70 }) }),
        companyName: z
          .string()
          .min(1, { message: t('myself.formulaire.errors.required') })
          .regex(noSpaceAllowedRule.regex, {
            message: t(noSpaceAllowedRule.message),
          })
          .regex(alphanumericAndSpecialCharactersRule.regex, {
            message: t(alphabeticAndSpecialCharactersRule.message),
          }),
        consent: z.boolean().optional(),
      })
      .superRefine((data, ctx) => {
        // Rendre `birthname` obligatoire si `civility` est Madame
        if (data.civility === 'Madame' && !data.birthname) {
          ctx.addIssue({
            code: 'custom',
            path: ['birthname'],
            message: t('myself.formulaire.errors.required'),
          })
        }
      }),
  })
}

const defaultMyselfData: MySelfFormData = {
  wrapper: {
    amount: null,
    savings: null,
    civility: '',
    firstname: '',
    lastname: '',
    birthname: '',
    birthday: '',
    birthCity: '',
    birthCounty: '',
    birthCountry: '',
    phone: '',
    mail: '',
    companyName: '',
    consent: false,
  },
}

const myselfFormData = useSessionStorage('myself_form', defaultMyselfData)

export type MySelfFormData = z.infer<ReturnType<typeof buildMyselfSchema>>

export const useMyselfData = () => {
  const { t } = useI18n()

  const myselfSchema = computed(() => buildMyselfSchema(t))
  const loading = ref<boolean>(false)

  return { myselfFormData, myselfSchema, defaultMyselfData, loading }
}
